import { Capacitor } from '@capacitor/core';
import axios from 'axios';

import { reportApiFailure } from '@sophocles/features/apiHealth/monitor';
import tokenService from '@sophocles/services/token';

// axiosClientFactory.js

export function createAxiosClient(store, options = {}) {
  const client = axios.create({
    baseURL: options.baseURL,
    headers: {
      Accept: 'application/json',
    },
    responseType: 'json',
    paramsSerializer: {
      // Serialize arrays as key=value1&key=value2 instead of key[]=value1&key[]=value2
      serialize: params => {
        const parts = [];
        Object.keys(params).forEach(key => {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach(v => {
              parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
            });
          } else if (value !== null && value !== undefined) {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
          }
        });
        return parts.join('&');
      },
    },
  });

  client.interceptors.request.use(
    config => {
      const { pool, idToken, isImpersonating, impersonationToken } = tokenService;

      if (pool) {
        config.headers['X-Pool-ID'] = pool;
      }

      if (options.socketId) {
        config.headers['Socket-ID'] = options.socketId();
      }

      // Check if running on a Capacitor native platform
      if (Capacitor.isNativePlatform()) {
        config.headers['X-Source'] = 'mobile';
      } else {
        config.headers['X-Source'] = 'web';
      }

      if (idToken) {
        if (isImpersonating && impersonationToken != null) {
          config.headers.Authorization = `Token ${impersonationToken}`;
        } else if (!pool) {
          config.headers.Authorization = `Token ${idToken}`;
        } else {
          config.headers.Authorization = `Bearer ${idToken}`;
        }
      }

      return config;
    },
    error => Promise.reject(error),
  );

  client.interceptors.response.use(
    response => response,
    async error => {
      // Server unreachable (no response — e.g. the brief gunicorn blip during a
      // tenant redeploy) or any 5xx: confirm via a /version/ probe and, if it's
      // really down, let ApiHealthGate take over. Guarding error.response also
      // avoids a crash reading `.status` off an undefined response.
      if (!error.response || error.response.status >= 500) {
        reportApiFailure(store);
        return Promise.reject(error);
      }

      const originalRequest = error.config;
      switch (error.response.status) {
        case 403:
          if (error.config.url.includes('/auth/refresh')) {
            return Promise.reject(error);
          }
          if (tokenService.refreshToken && tokenService.refreshToken !== 'undefined') {
            if (!originalRequest._retry) {
              originalRequest._retry = true;
              tokenService.idToken = null;
              await options.onUnauthenticated();
              return client(originalRequest);
            }
          }

          if (error.config.url.includes('/users/me') && !window.location.href.includes('/login')) {
            tokenService.logout();
            window.location.href = '/login';
          }

          return Promise.reject(error);
        default:
          return Promise.reject(error);
      }
    },
  );

  return client;
}
